/**
 * Auth store. Tokens persist to localStorage so a refresh keeps the session.
 * Phase 7 may move to HTTP-only cookies once the API supports them; the
 * surface here is small enough to swap later.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Role = 'user' | 'parish_admin' | 'super_admin';

export type CurrentUser = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  baptism_name: string | null;
  role: Role;
  fasting_level: 'beginner' | 'standard' | 'strict';
  prayer_rule_level: 'beginner' | 'standard' | 'full';
  parish_id: number | null;
  locale: string;
  timezone: string;
  calendar_style: 'new' | 'old' | 'revised_julian';
  patron_saint_slug: string | null;
  email_verified_at: string | null;
};

type State = {
  accessToken: string | null;
  refreshToken: string | null;
  user: CurrentUser | null;
};

type Actions = {
  setSession: (s: { access_token: string; refresh_token: string; user: CurrentUser }) => void;
  setUser: (u: CurrentUser) => void;
  clear: () => void;
};

export const useAuth = create<State & Actions>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setSession: ({ access_token, refresh_token, user }) =>
        set({ accessToken: access_token, refreshToken: refresh_token, user }),
      setUser: (u) => set({ user: u }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
    }),
    {
      name: 'orthodox-way:auth',
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
      }),
    },
  ),
);

export function isAdmin(user: CurrentUser | null): boolean {
  return user?.role === 'parish_admin' || user?.role === 'super_admin';
}

export function isSuperAdmin(user: CurrentUser | null): boolean {
  return user?.role === 'super_admin';
}
